import { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { nigerianStates } from '@/data/nigerianStates';
import { validateNigerianState } from '@/utils/authHelpers';
import { logAdminAction } from '@/utils/adminAudit';

interface FormData {
  fullName: string;
  phoneNumber: string;
  email: string;
  state: string;
  localGovernment: string;
  subGroup: string;
  ageGroup: string;
  gender: string;
  isAdmin: boolean;
}

const ageGroups = ['18-25', '26-35', '36-45', '46-55', '56-65', '65+'];
const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' }
];

const AdminRegistration = () => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    phoneNumber: '',
    email: '',
    state: '',
    localGovernment: '',
    subGroup: '',
    ageGroup: '',
    gender: '',
    isAdmin: false
  });
  
  const [lgas, setLgas] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const handleStateChange = (value: string) => {
    if (!validateNigerianState(value)) {
      toast.error('Please select a valid Nigerian state');
      return;
    }
    
    setFormData({
      ...formData,
      state: value,
      localGovernment: ''
    });
    
    const fetchLGAs = async () => {
      try {
        const { data, error } = await supabase
          .from('community_members')
          .select('local_government')
          .eq('state', value)
          .order('local_government');
          
        if (error) throw error;
        
        const uniqueLgas = Array.from(
          new Set(data.map(item => item.local_government))
        ).filter(Boolean) as string[];
        
        setLgas(uniqueLgas);
      } catch (error) {
        console.error('Error fetching LGAs:', error);
        toast.error('Failed to fetch local governments');
      }
    };
    
    fetchLGAs();
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = value.trim();
    
    setFormData({
      ...formData,
      [name]: sanitizedValue
    });
  };
  
  const handleSelectChange = (name: keyof FormData, value: string) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleCheckboxChange = (checked: boolean) => {
    setFormData({
      ...formData,
      isAdmin: checked
    });
  };
  
  const validateForm = () => {
    if (!formData.fullName || formData.fullName.length < 2) {
      toast.error('Full name must be at least 2 characters');
      return false;
    }
    
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    
    if (!formData.phoneNumber || formData.phoneNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    
    if (!validateNigerianState(formData.state)) {
      toast.error('Please select a valid Nigerian state');
      return false;
    }
    
    if (!formData.localGovernment) {
      toast.error('Please select a local government area');
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { data: existingMembers, error: memberError } = await supabase
        .from('community_members')
        .select('id, email')
        .eq('email', formData.email);
        
      if (memberError) {
        throw memberError;
      }
      
      if (existingMembers && existingMembers.length > 0) {
        toast.error('A member with this email already exists');
        return;
      }
      
      const userId = crypto.randomUUID();
      
      const { data: memberData, error: insertError } = await supabase
        .from('community_members')
        .insert({
          id: userId,
          full_name: formData.fullName,
          phone_number: formData.phoneNumber,
          email: formData.email,
          state: formData.state,
          local_government: formData.localGovernment,
          sub_group: formData.subGroup || null,
          age_group: formData.ageGroup || null,
          gender: formData.gender || null,
          approved: false
        })
        .select()
        .single();
        
      if (insertError) {
        throw insertError;
      }
      
      await logAdminAction({
        action: 'CREATE_MEMBER',
        targetTable: 'community_members',
        targetId: userId,
        newValues: memberData
      });
      
      const roleToAdd = formData.isAdmin ? 'admin' : 'member';
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: userId,
          role: roleToAdd,
          approved: false
        });
        
      if (roleError) {
        console.error('Error adding role:', roleError);
        toast.error(`Member added but failed to assign ${roleToAdd} role`);
      } else {
        await logAdminAction({
          action: 'ASSIGN_ROLE',
          targetTable: 'user_roles',
          targetId: userId,
          newValues: { role: roleToAdd, approved: false }
        });
      }
      
      const roleMessage = formData.isAdmin ? 'admin' : 'member';
      toast.success(`${roleMessage} registered successfully. Must be approved before they can access the community.`);
      
      setFormData({
        fullName: '',
        phoneNumber: '',
        email: '',
        state: '',
        localGovernment: '',
        subGroup: '',
        ageGroup: '',
        gender: '',
        isAdmin: false
      });
      setLgas([]);
      
    } catch (error) {
      console.error('Error registering member:', error);
      toast.error('Failed to register member. Please check all fields and try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-gray-900">Register New Member</CardTitle>
        <CardDescription className="text-gray-700">
          Add a new member to the community (requires admin approval)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName" className="text-gray-900 font-medium">Full Name *</Label>
            <Input 
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              required
              minLength={2}
              maxLength={100}
              className="text-gray-900 bg-white border-gray-300"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phoneNumber" className="text-gray-900 font-medium">Phone Number *</Label>
            <Input 
              id="phoneNumber" 
              name="phoneNumber"
              type="tel"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              required
              minLength={10}
              maxLength={15}
              className="text-gray-900 bg-white border-gray-300"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-900 font-medium">Email Address *</Label>
            <Input 
              id="email" 
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              maxLength={255}
              className="text-gray-900 bg-white border-gray-300"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="state" className="text-gray-900 font-medium">State of Residence *</Label>
              <Select
                value={formData.state}
                onValueChange={(value) => handleStateChange(value)}
                required
              >
                <SelectTrigger id="state" className="text-gray-900 bg-white border-gray-300">
                  <SelectValue placeholder="Select State" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {nigerianStates.map((state) => (
                    <SelectItem key={state} value={state} className="text-gray-900">
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="localGovernment" className="text-gray-900 font-medium">Local Government *</Label>
              <Select
                value={formData.localGovernment}
                onValueChange={(value) => handleSelectChange('localGovernment', value)}
                disabled={!formData.state}
                required
              >
                <SelectTrigger id="localGovernment" className="text-gray-900 bg-white border-gray-300">
                  <SelectValue placeholder="Select LGA" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {lgas.map((lga) => (
                    <SelectItem key={lga} value={lga} className="text-gray-900">
                      {lga}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ageGroup" className="text-gray-900 font-medium">Age Group</Label>
              <Select
                value={formData.ageGroup}
                onValueChange={(value) => handleSelectChange('ageGroup', value)}
              >
                <SelectTrigger id="ageGroup" className="text-gray-900 bg-white border-gray-300">
                  <SelectValue placeholder="Select Age Group" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {ageGroups.map((group) => (
                    <SelectItem key={group} value={group} className="text-gray-900">
                      {group}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="gender" className="text-gray-900 font-medium">Gender</Label>
              <Select
                value={formData.gender}
                onValueChange={(value) => handleSelectChange('gender', value)}
              >
                <SelectTrigger id="gender" className="text-gray-900 bg-white border-gray-300">
                  <SelectValue placeholder="Select Gender" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {genderOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-gray-900">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="subGroup" className="text-gray-900 font-medium">Sub-Group (Optional)</Label>
            <Input 
              id="subGroup" 
              name="subGroup"
              value={formData.subGroup}
              onChange={handleInputChange}
              maxLength={100}
              className="text-gray-900 bg-white border-gray-300"
            />
          </div>
          
          <div className="flex items-center space-x-2 pt-2">
            <Checkbox 
              id="isAdmin" 
              checked={formData.isAdmin} 
              onCheckedChange={handleCheckboxChange}
            />
            <Label htmlFor="isAdmin" className="cursor-pointer text-gray-900 font-medium">
              Register as Admin (requires approval)
            </Label>
          </div>
          
          <Button 
            type="submit" 
            disabled={isLoading} 
            className="w-full bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90 text-white"
          >
            {isLoading ? 'Registering...' : 'Register Member'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default AdminRegistration;
